import { debounce } from 'lodash'
import { useCallback, useEffect, useRef } from 'react'

type DebounceOptions = {
  leading?: boolean
  trailing?: boolean
  maxWait?: number
}

export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
  options?: DebounceOptions,
) {
  const callbackRef = useRef(callback)

  // Update ref when callback changes
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const debouncedFn = useCallback(
    debounce(
      (...args: Parameters<T>) => {
        callbackRef.current(...args)
      },
      delay,
      options,
    ),
    [delay, options?.leading, options?.trailing, options?.maxWait],
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedFn.cancel()
    }
  }, [debouncedFn])

  return debouncedFn
}
